import React from "react";

import Decimal from "decimal.js";
import {produce} from "immer";
import {startCase} from "lodash-es";

import * as constants from "@client/constants";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";
import * as utils from "@client/utils";

import {Icon} from "@client/components/Icon";
import {ManagerTable} from "@client/components/ManagerTable";
import * as widgets from "@client/forms/widgets";
import {ManagerLayout, ManagerTabs} from "@client/site/ManagerLayout";
import * as forms from "@client/site/forms";
import {reverse, server} from "@reactivated";

import {HabitStatus, MetricStatus, NoData, WorkoutStatus} from "./ClientDashboard";
import {ClientNavigation} from "./ClientManagerPlanner";
import {useProgram} from "./UpdateProgram";

function DateRangeSelector(props: {
    dateFrom: string;
    dateTo: string;
    baseUrl: string;
    hasCustomRange: boolean;
}) {
    const [dateFrom, setDateFrom] = React.useState(props.dateFrom);
    const [dateTo, setDateTo] = React.useState(props.dateTo);

    const buildUrl = (from: string, to: string) => {
        const params = new URLSearchParams();
        if (from) params.set("date_from", from);
        if (to) params.set("date_to", to);
        const queryString = params.toString();
        return queryString ? `${props.baseUrl}?${queryString}` : props.baseUrl;
    };

    const handleDateFromChange = (value: string) => {
        setDateFrom(value);
        window.location.href = buildUrl(value, dateTo);
    };

    const handleDateToChange = (value: string) => {
        setDateTo(value);
        window.location.href = buildUrl(dateFrom, value);
    };

    const handleClear = () => {
        window.location.href = props.baseUrl;
    };

    return (
        <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
                <label
                    htmlFor="date-from"
                    className="text-sm font-medium text-gray-700"
                >
                    From
                </label>
                <input
                    type="date"
                    id="date-from"
                    value={dateFrom}
                    onChange={(e) => handleDateFromChange(e.target.value)}
                    className="
                        rounded-md border-0 px-3 py-1.5 text-gray-900 ring-1
                        ring-gray-300 ring-inset
                        focus:ring-2 focus:ring-indigo-600
                        sm:text-sm
                    "
                />
            </div>
            <div className="flex items-center gap-2">
                <label htmlFor="date-to" className="text-sm font-medium text-gray-700">
                    To
                </label>
                <input
                    type="date"
                    id="date-to"
                    value={dateTo}
                    onChange={(e) => handleDateToChange(e.target.value)}
                    className="
                        rounded-md border-0 px-3 py-1.5 text-gray-900 ring-1
                        ring-gray-300 ring-inset
                        focus:ring-2 focus:ring-indigo-600
                        sm:text-sm
                    "
                />
            </div>
            {props.hasCustomRange && (
                <button
                    type="button"
                    onClick={handleClear}
                    className="
                        text-sm text-indigo-600
                        hover:text-indigo-500
                    "
                >
                    Clear
                </button>
            )}
        </div>
    );
}

function AssignProgram(props: {
    exercise_maxes: server.manager.templates.ClientManagerDetail["exercise_maxes"];
    program_templates: server.manager.templates.ListWorkoutProgram.output[];
    client_manager: server.manager.templates.ClientManagerDetail["client_manager"];
    onSelectProgram: (program: server.journal.workouts.Program.output) => void;
}) {
    const isImperial = forms.useIsImperial();
    const [template, setTemplate] =
        React.useState<server.manager.templates.ListWorkoutProgram.output | null>(
            props.program_templates[0],
        );
    const [maxes, setMaxes] = React.useState(
        props.exercise_maxes.actual.reduce<Record<UUID, DecimalValue>>(
            (accumulator, max) => {
                accumulator[max.exercise_type.uuid] = max.weight;
                return accumulator;
            },
            {},
        ),
    );

    const onEmpty = async () => {
        const result = await server.journal.workouts.select_program_template({
            user_id: props.client_manager.client.id,
            source_template: "BLANK",
            should_sync: false,
            exercise_maxes: [],
        });

        if (result.type == "success") {
            props.onSelectProgram(result.data);
        }
    };
    const [values, setValues] = React.useState({should_sync: true});
    const form = forms.usePform(
        {
            should_sync: {
                type: "boolean",
            },
        },
        values,
        setValues,
    );

    const onAssign = async () => {
        if (template == null) {
            return;
        }

        const result = await server.journal.workouts.select_program_template({
            user_id: props.client_manager.client.id,
            source_template: template.uuid,
            should_sync: values.should_sync,
            exercise_maxes: Object.entries(maxes).map(
                ([exerciseTypeUUID, exerciseTypeWeight]) => ({
                    type: "MAX",
                    reps: 1,
                    weight: exerciseTypeWeight,
                    workout: null,
                    exercise_type: {uuid: exerciseTypeUUID as UUID},
                }),
            ),
        });

        if (result.type == "success") {
            props.onSelectProgram(result.data);
        }
    };

    return (
        <div className="space-y-4">
            <div className="mt-2 flex items-center">
                <h2 className="flex-1 text-xl">Assign a program</h2>
                <typography.AsyncButton
                    type="button"
                    className="
                        rounded-sm bg-white px-2 py-1 text-sm font-semibold
                        text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset
                        hover:bg-gray-50
                    "
                    onClick={onEmpty}
                >
                    <Icon icon="0823-plus">Empty program</Icon>
                </typography.AsyncButton>
            </div>
            <div>
                <label
                    htmlFor="location"
                    className="block text-sm/6 font-medium text-gray-900"
                >
                    Choose program template
                </label>
                <select
                    id="location"
                    name="location"
                    className="
                        form-select mt-2 block w-full rounded-md border-0 py-1.5 pl-3
                        text-gray-900 ring-1 ring-gray-300 ring-inset
                        focus:ring-2 focus:ring-indigo-600
                        sm:text-sm/6
                    "
                    disabled={props.program_templates.length == 0}
                    value={template?.uuid ?? ""}
                    onChange={(event) => {
                        setTemplate(
                            props.program_templates.find(
                                (t) => t.uuid == event.currentTarget.value,
                            ) ?? null,
                        );
                    }}
                >
                    {props.program_templates.map((t) => (
                        <option key={t.uuid} value={t.uuid}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>
            {template != null && template.exercise_type_maxes_required.length > 0 && (
                <fieldset className="rounded-md border border-gray-300 px-6 py-4">
                    <div className="mb-2">
                        <h3 className="text-lg">Exercise maxes</h3>
                        <p className="text-sm">
                            This program uses percentages. Enter the client’s maxes.
                        </p>
                    </div>

                    <div
                        className="
                            grid grid-cols-1 gap-4
                            sm:grid-cols-2
                        "
                    >
                        {template.exercise_type_maxes_required.map((exerciseType) => (
                            <label key={exerciseType.uuid}>
                                {exerciseType.name}
                                <widgets.WeightInput
                                    isImperial={isImperial}
                                    name={exerciseType.uuid}
                                    value={maxes[exerciseType.uuid]}
                                    onChange={(value) => {
                                        setMaxes(
                                            produce(maxes, (draft) => {
                                                if (value == null) {
                                                    delete draft[exerciseType.uuid];
                                                } else {
                                                    draft[exerciseType.uuid] = value;
                                                }
                                            }),
                                        );
                                    }}
                                />
                            </label>
                        ))}
                    </div>
                </fieldset>
            )}
            <div className="flex gap-8">
                <typography.AsyncButton
                    disabled={template == null}
                    onClick={onAssign}
                    className="
                        rounded-sm bg-indigo-600 px-2 py-1 text-sm font-semibold
                        text-white shadow-xs
                        hover:bg-indigo-500
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-indigo-600 focus-visible:outline-solid
                        disabled:opacity-70
                    "
                >
                    Assign
                </typography.AsyncButton>
                <div className="flex items-center gap-2">
                    <forms.PWidget field={form.fields.should_sync} />
                    <forms.Label error={null} label="Program updates should sync" />
                </div>
            </div>
        </div>
    );
}

function ClientProgram(props: {
    program: server.journal.workouts.Program.output;
    setProgram: React.Dispatch<
        React.SetStateAction<server.journal.workouts.Program.output>
    >;
    client_manager: server.manager.templates.ClientManagerDetail["client_manager"];
    readOnly?: boolean;
}) {
    const {content} = useProgram({
        program: props.program,
        setProgram: props.setProgram,
        embed: {
            url:
                reverse("client_detail", {
                    client_manager_id: props.client_manager.id,
                }) + "?tab=program",
            readOnly: props.readOnly,
            user_id: props.client_manager.client.id,
        },
        clients: [],
    });

    return (
        <div>
            <h1 className="text-2xl font-medium">{props.program.name}</h1>
            {content}
        </div>
    );
}

function ProgramDetail(props: {
    past_program: server.journal.workouts.PastProgram.output;
    client_manager: server.manager.templates.ClientManagerDetail["client_manager"];
}) {
    const [program, setProgram] =
        React.useState<server.journal.workouts.Program.output | null>(null);

    React.useEffect(() => {
        void server.journal.workouts
            .program_detail({form: props.past_program.program.uuid})
            .then((result) => {
                if (result.type == "success") {
                    setProgram(result.data);
                }
            });
    }, [props.past_program.program.uuid]);

    if (program == null) {
        return <div>Loading...</div>;
    }

    return (
        <ClientProgram
            program={produce(program, (draft) => {
                draft.name = `${draft.name} (Archived)`;
            })}
            setProgram={() => {}}
            client_manager={props.client_manager}
            readOnly
        />
    );
}

export const Template = (props: server.manager.templates.ClientManagerDetail) => {
    const [program, setProgram] = React.useState(props.program);
    const [pastPrograms, setPastPrograms] = React.useState(props.past_programs);
    const [programRoute, setProgramRoute] = React.useState<"program" | "archive">(
        "program",
    );
    const [pastProgram, setPastProgram] =
        React.useState<server.journal.workouts.PastProgram.output | null>(null);

    const coreMetricNames: server.manager.templates.ClientManagerDetail["metrics"] =
        constants.CORE_METRICS.map(([metricName]) => metricName);

    const metrics = constants.ALL_METRICS.filter(
        ([metricName]) =>
            props.metrics.includes(metricName) ||
            (metricName != "net_carbs" && coreMetricNames.includes(metricName)),
    );

    const columns = [{name: "Date", accessor: "date"}]
        .concat(
            props.primary_metric != null
                ? [{name: props.primary_metric.label, accessor: "primary_metric"}]
                : [],
        )
        .concat(props.show_habits ? [{name: "Habits", accessor: "habits"}] : [])
        .concat(
            metrics.map(([metric, , , , metricLabel]) => ({
                name: startCase(metricLabel),
                accessor: metric,
            })),
        );

    const values = props.statuses.map((status, index) =>
        metrics.reduce<Record<string, React.ReactNode>>(
            (accumulator, [metric, , , unit]) => ({
                ...accumulator,
                [metric]: (
                    <MetricStatus
                        noStatus={index === 0}
                        status={status[metric].status}
                        total={status[metric].total}
                        target={status[metric].target}
                        unit={unit}
                    />
                ),
            }),
            {
                date: status.date != null ? utils.formatDate(status.date) : "",
                habits: (
                    <>
                        {(["SLEEP", "STEPS", "WATER", "GRATITUDE"] as const).map(
                            (habit) => (
                                <HabitStatus
                                    key={habit}
                                    noStatus={index === 0}
                                    status={status[habit].status}
                                    total={status[habit].total}
                                    target={status[habit].target}
                                    habit={habit}
                                />
                            ),
                        )}
                        <WorkoutStatus status={status.logged_workout} />
                    </>
                ),
                link:
                    status.date != null
                        ? reverse("client_days", {
                              client_manager_id: props.client_manager.id,
                              date: status.date,
                          })
                        : "",
                primary_metric:
                    status.primary_metric?.value != null ? (
                        utils.formatDecimalAndUnit(
                            new Decimal(status.primary_metric.value).toDecimalPlaces(1),
                            status.primary_metric.metric.type,
                            props.client_manager.client.profile.is_imperial,
                        )
                    ) : (
                        <NoData />
                    ),
            },
        ),
    );

    const baseUrl = reverse("client_detail", {
        client_manager_id: props.client_manager.id,
    });

    const journal = (
        <form
            noValidate
            method="POST"
            action=""
            className="pt-2"
            style={{display: "flex", flexDirection: "column", gap: 20}}
        >
            <forms.CSRFToken />
            <ClientNavigation>
                <typography.ButtonGroup>
                    <typography.Button
                        href={reverse("any_client_delete", {
                            client_manager_id: props.client_manager.id,
                        })}
                    >
                        Delete
                    </typography.Button>
                    <typography.Button
                        href={
                            reverse("client_detail", {
                                client_manager_id: props.client_manager.id,
                            }) + "?export"
                        }
                        track="EXPORTED_CLIENT_DATA"
                    >
                        Download
                    </typography.Button>
                    <typography.Button
                        href={reverse("client_planner", {
                            client_manager_id: props.client_manager.id,
                        })}
                    >
                        Planner
                    </typography.Button>
                    <typography.Button
                        href={reverse("client_check_ins", {
                            client_manager_id: props.client_manager.id,
                        })}
                    >
                        Check-ins
                    </typography.Button>
                    <typography.Button
                        href={reverse("client_reports", {
                            client_manager_id: props.client_manager.id,
                        })}
                    >
                        Reports
                    </typography.Button>
                    {props.has_survey && (
                        <typography.Button
                            href={reverse("client_survey", {
                                client_manager_id: props.client_manager.id,
                            })}
                        >
                            Survey
                        </typography.Button>
                    )}
                </typography.ButtonGroup>
                <DateRangeSelector
                    dateFrom={props.date_from}
                    dateTo={props.date_to}
                    baseUrl={baseUrl}
                    hasCustomRange={props.has_custom_date_range}
                />
            </ClientNavigation>
            <ManagerTable columns={columns} values={values} />
        </form>
    );

    const profile = (
        <form method="POST" action="" className="flex flex-col gap-4 pt-2">
            <forms.CSRFToken />
            <forms.Fields form={props.profile_form} />
            <typography.Button submit>Update</typography.Button>
        </form>
    );

    const disableSync = async () => {
        if (program == null || program.program_users == null) {
            return;
        }

        if (
            window.confirm(
                "If you disable sync, this user's programming will not update automatically based on your changes to the master template.\n\nThis cannot be turned back on.",
            )
        ) {
            void analytics.track("DISABLED_PROGRAM_SYNC");
            await server.journal.workouts.disable_sync({
                source_program: program.uuid,
            });
            setProgram({
                ...program,
                program_users: {...program.program_users, should_sync: false},
            });
        }
    };

    const archiveProgram = async () => {
        if (program == null) {
            return;
        }
        void analytics.track("ARCHIVED_PROGRAM");
        await server.journal.workouts.archive_program({
            source_program: program.uuid,
        });
        setProgram(null);
        setPastPrograms([
            {id: 0, program: program, completed_on: null},
            ...pastPrograms,
        ]);
    };

    const pastProgramDetail =
        pastProgram != null ? (
            <ProgramDetail
                past_program={pastProgram}
                client_manager={props.client_manager}
            />
        ) : null;

    const pastProgramsList = (
        <div className="space-y-2">
            <h2 className="text-xl">Past Programs</h2>
            {pastPrograms.map((programUser) => (
                <button
                    key={programUser.id}
                    onClick={() => setPastProgram(programUser)}
                    className="
                        relative w-full rounded-lg border border-gray-200 bg-white
                        shadow-xs
                    "
                >
                    <div
                        className="
                            flex w-full items-center justify-between space-x-6 p-4
                        "
                    >
                        <div className="flex-1 truncate">
                            <div className="flex items-center space-x-3">
                                <h3
                                    className="
                                        truncate text-sm font-medium text-gray-900
                                    "
                                >
                                    {programUser.program.name} –{" "}
                                    {utils.formatDate(programUser.program.updated_at)}
                                </h3>
                            </div>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );

    return (
        <ManagerLayout
            title={props.client_manager.name}
            currentClientManagerId={props.client_manager.id}
        >
            <typography.Header>{props.client_manager.name}</typography.Header>

            <ManagerTabs
                tabs={[
                    {
                        name: "journal",
                        title: "Journal",
                        content: journal,
                        icon: "custom-0283-contacts",
                    },
                    {
                        name: "profile",
                        title: "Profile",
                        content: profile,
                        icon: "user",
                    },
                    {
                        name: "program",
                        title: "Program",
                        options: (
                            <div
                                className="
                                    my-2 flex flex-col gap-1
                                    sm:my-0 sm:flex-row
                                "
                            >
                                {program != null && (
                                    <>
                                        {program.program_users?.should_sync == true && (
                                            <button
                                                type="button"
                                                className="
                                                    rounded-sm bg-white px-2 py-1
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                                onClick={disableSync}
                                            >
                                                Disable sync
                                            </button>
                                        )}

                                        {programRoute == "program" && (
                                            <button
                                                type="button"
                                                className="
                                                    rounded-sm bg-white px-2 py-1
                                                    text-xs font-semibold text-gray-900
                                                    shadow-xs ring-1 ring-gray-300
                                                    ring-inset
                                                    hover:bg-gray-50
                                                "
                                                onClick={archiveProgram}
                                            >
                                                Archive program
                                            </button>
                                        )}
                                    </>
                                )}
                                {programRoute == "program" ? (
                                    <button
                                        type="button"
                                        className="
                                            rounded-sm bg-white px-2 py-1 text-xs
                                            font-semibold text-gray-900 shadow-xs ring-1
                                            ring-gray-300 ring-inset
                                            hover:bg-gray-50
                                        "
                                        onClick={() => setProgramRoute("archive")}
                                    >
                                        Past programs
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="
                                            rounded-sm bg-white px-2 py-1 text-xs
                                            font-semibold text-gray-900 shadow-xs ring-1
                                            ring-gray-300 ring-inset
                                            hover:bg-gray-50
                                        "
                                        onClick={() => {
                                            setPastProgram(null);
                                            setProgramRoute("program");
                                        }}
                                    >
                                        Current program
                                    </button>
                                )}
                            </div>
                        ),
                        content:
                            pastProgram != null ? (
                                pastProgramDetail
                            ) : programRoute == "archive" ? (
                                pastProgramsList
                            ) : program == null ? (
                                <AssignProgram
                                    {...props}
                                    onSelectProgram={(program) => setProgram(program)}
                                />
                            ) : (
                                <ClientProgram
                                    {...props}
                                    program={program}
                                    setProgram={(value) => {
                                        if (typeof value == "function") {
                                            setProgram((program) =>
                                                program == null
                                                    ? program
                                                    : value(program),
                                            );
                                        } else {
                                            setProgram(value);
                                        }
                                    }}
                                />
                            ),
                        icon: "custom-dumbbell",
                    },
                ]}
            />
        </ManagerLayout>
    );
};
